import React, { useState } from 'react';
import './AdvisorHeader.css';

/*
 * The co-branded chat header for a company's advisor.
 *
 * The logo sits in a white rounded chip. The header is navy, and a chip
 * guarantees contrast for whatever a company sends us — a dark wordmark, a
 * transparent PNG, a logo with its own light background — rather than asking
 * every company for a light variant they may not have.
 *
 * `companyName` is always present, so the header is never blank: with no logo
 * it renders as text, and if the logo fails to load it falls back to the same
 * text rather than leaving a broken-image gap.
 *
 * "Powered by AI Broker" stays visible but quiet. The company is the host here;
 * we are the infrastructure.
 *
 * WhatsApp sits beside it as a way to continue, not a way in — the buyer is
 * already talking to us, and this only moves the same conversation, scoped to
 * the same brokerage, into the app they keep on their home screen. It appears
 * only when the server sends a link; a number that is not configured yet must
 * show nothing rather than a button that goes nowhere.
 */
export default function AdvisorHeader({ companyName, logoUrl, whatsappUrl, onBack }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = Boolean(logoUrl) && !logoFailed;

  return (
    <header className="advisor-header">
      <div className="advisor-header__brand">
        {showLogo ? (
          <span className="advisor-header__chip">
            <img
              src={logoUrl}
              alt={companyName}
              onError={() => setLogoFailed(true)}
              className="advisor-header__logo"
            />
          </span>
        ) : (
          <span className="advisor-header__wordmark">{companyName}</span>
        )}

        <span className="advisor-header__role">Property advisor</span>
      </div>

      <div className="advisor-header__aside">
        {whatsappUrl && (
          <a
            className="advisor-header__whatsapp"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5 0-.2 0-.4 0-.5 0-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.4.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3z"
              />
              <path
                fill="currentColor"
                d="M12 2A10 10 0 0 0 3.5 17.2L2 22l4.9-1.5A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-2.9.9.9-2.8-.2-.3A8.2 8.2 0 1 1 12 20.2z"
              />
            </svg>
            Continue on WhatsApp
          </a>
        )}
        <span className="advisor-header__powered">Powered by AI Broker</span>
      </div>
    </header>
  );
}
